<template>
  <a-modal
      :open="open"
      @cancel="closeForm"
      :footer="null"
      class="modal-no-padding reference-profile-modal"
  >
    <div class="modal-no-padding__header">
      <h2>{{ isEditMode ? ('Добавление сотрудников: ' + profileTitle) : 'Создание эталонного профиля' }}</h2>
    </div>

    <a-divider style="margin: 16px 0" />

    <div v-if="currentStep === 'form'">
      <div class="modal-no-padding__body">
        <a-form name="referenceProfile" layout="vertical" :model="form" @finish="goToConfirmStep">
          <a-form-item
              v-if="!isEditMode"
              label="Название"
              name="title"
              :rules="[{ required: true, message: 'Введите название' }]"
          >
            <a-input v-model:value="form.title" :status="statusError.title ? 'error' : ''" />
            <span v-if="statusError.title" class="field-error">{{ statusError.title }}</span>
          </a-form-item>

          <a-row>
            <a-flex class="upload-block" vertical>
              <a
                  :href="sampleFile"
                  target="_blank"
                  download
                  class="template-link"
              >
                Скачать excel-шаблон
              </a>

              <a-upload
                  v-if="!fileList[0]?.name"
                  :before-upload="beforeUpload"
                  :file-list="fileList"
                  :max-count="1"
                  :show-upload-list="false"
                  accept=".xls,.xlsx"
              >
                <a-button :loading="fileLoading" class="secondary-button">
                  <template #icon>
                    <UploadOutlined />
                  </template>
                  {{ fileList.length ? 'Заменить' : 'Выбрать файл' }}
                </a-button>
              </a-upload>

              <div v-else class="file-row">
                <PaperClipOutlined />

                <span class="file-name">
                  {{ fileList[0]?.name }}
                </span>

                <a-upload
                    :before-upload="beforeUpload"
                    :file-list="fileList"
                    :max-count="1"
                    :show-upload-list="false"
                    accept=".xls,.xlsx"
                >
                  <a-button type="link" class="replace-btn" :loading="fileLoading">
                    Заменить
                  </a-button>
                </a-upload>
              </div>

              <div v-if="hasParsedTotals && !fileLoading" class="after-upload">
                <div class="totals-row">
                  <span>Всего сотрудников в файле</span>
                  <span>{{ parsedTotals.totalUsersInFiles ?? 0 }}</span>
                </div>

                <div class="totals-row">
                  <span>Сотрудников соотнеслось</span>
                  <span>
                    {{
                      (parsedTotals.success ?? 0)
                      + (parsedTotals.noPassing ?? 0)
                      + (parsedTotals.passingsNotValid ?? 0)
                    }}
                  </span>
                </div>

                <div class="totals-row muted-status" :class="{ 'has-warning': parsedTotals.noPassing }">
                  <span>Не прошли тестирование</span>
                  <span>{{ parsedTotals.noPassing ?? 0 }}</span>
                </div>

                <div class="totals-row muted-status" :class="{ 'has-warning': parsedTotals.passingsNotValid }">
                  <span>
                    Исключены аномалии
                    <a-popover placement="right" :overlay-style="{ width: '210px', maxWidth: '210px' }">
                      <template #content>
                        <span>Исключаем результаты прохождения оценки менее 15 мин и более 2 ч — они ненадёжны из-за нарушения процедуры.</span>
                      </template>
                      <QuestionCircleOutlined class="info-icon" />
                    </a-popover>
                  </span>
                  <span>{{ parsedTotals.passingsNotValid ?? 0 }}</span>
                </div>

                <div v-if="parsedTotals.parsedWithErrors" class="totals-row error-status">
                  <span>
                    Ошибки в файле
                    <a-popover placement="top">
                      <template #content>
                        <span>Часть строк не удалось обработать: сотрудник не найден.</span>
                      </template>
                      <InfoCircleOutlined class="warning-icon" />
                    </a-popover>
                  </span>
                  <span>{{ parsedTotals.parsedWithErrors ?? 0 }}</span>
                </div>

                <a-divider class="totals-divider" />

                <div class="totals-row total-row">
                  <span class="total-count">Итого сотрудников для расчета</span>
                  <span>{{ parsedTotals.success ?? 0 }}</span>
                </div>

                <a
                    v-if="parsedUsers?.file_url"
                    :href="parsedUsers?.file_url"
                    download
                    target="_blank"
                    class="download-report"
                >
                  Скачать отчет
                </a>
              </div>
            </a-flex>
          </a-row>
        </a-form>
      </div>

      <a-divider style="margin: 16px 0" />

      <div class="modal-no-padding__footer">
        <div class="button-row">
          <a-button class="secondary-button" @click="closeForm">Отменить</a-button>
          <a-button
              class="primary-button"
              :disabled="submitDisabled"
              :loading="fileLoading"
              @click="goToConfirmStep"
          >
            {{ primaryButtonText }}
          </a-button>
        </div>
      </div>
    </div>

    <div v-if="currentStep === 'confirm'">
      <div class="modal-no-padding__body">
        <div class="confirm-text">
          <template v-if="isEditMode">
            <p>Вы уверены, что хотите добавить сотрудников?</p>
          </template>
          <template v-else>
            <p>Для создания будет использован 1 доступ.</p>
            <p>Вы уверены, что хотите создать эталонный профиль?</p>
          </template>
        </div>

        <div class="confirm-details">
          <div class="confirm-row">
            <span>Название:</span>
            <span class="confirm-data">{{ referenceTitle }}</span>
          </div>

          <template v-if="isEditMode">
            <div class="confirm-row">
              <span>Сотрудников в профиле:</span>
              <span class="confirm-data">{{ parsedTotals.totalReportUsers ?? 0 }}</span>
            </div>

            <div class="confirm-row">
              <span>Итого сотрудников для добавления:</span>
              <span class="confirm-data">{{ parsedTotals.success ?? 0 }}</span>
            </div>
          </template>

          <template v-else>
            <div class="confirm-row">
              <span>Итого сотрудников для расчета:</span>
              <span class="confirm-data">{{ parsedTotals.success ?? 0 }}</span>
            </div>
          </template>
        </div>
      </div>

      <a-divider style="margin: 16px 0" />

      <div class="modal-no-padding__footer">
        <div class="button-row">
          <a-button class="secondary-button back-button" @click="backToForm">Назад</a-button>
          <a-button class="secondary-button" @click="closeForm">Отменить</a-button>
          <a-button
              class="primary-button"
              :disabled="!parsedTotals?.usersToAdd"
              :loading="referenceFormSubmitted"
              @click="submitReferenceProfile"
          >
            {{ primaryButtonText }}
          </a-button>
        </div>
      </div>
    </div>

    <div class="form-successful" v-if="currentStep === 'result'">
      <div class="modal-no-padding__body">
        <div class="successful-text">
          <template v-if="successfullySubmitted">
            <SmileOutlined class="happy-smile" />
            <p>Эталонный профиль формируется</p>
            <span>
              Будет готов в течение 5 минут. Его можно найти на странице «Эталонный профиль»
            </span>
          </template>

          <template v-if="unknownError">
            <FrownOutlined class="frown-smile" />
            <p>Ошибка формирования эталонного профиля</p>
            <span>
              Не удалось сохранить данные. Попробуйте ещё раз или обратитесь к администратору.
            </span>
          </template>
        </div>
      </div>

      <a-divider style="margin: 16px 0" />

      <div class="modal-no-padding__footer">
        <div class="button-row">
          <a-button class="secondary-button" @click="closeForm">Закрыть</a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import {
  FrownOutlined,
  PaperClipOutlined,
  QuestionCircleOutlined,
  SmileOutlined,
  UploadOutlined,
  WarningOutlined,
  InfoCircleOutlined
} from '@ant-design/icons-vue';
import { computed, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import useBreakpoints from '@/mixins/useBreakpoints';

export default {
  name: 'reference-profile-form',
  components: {
    FrownOutlined,
    PaperClipOutlined,
    QuestionCircleOutlined,
    SmileOutlined,
    UploadOutlined,
    WarningOutlined,
    InfoCircleOutlined
  },
  props: {
    open: Boolean,
    profileId: {
      type: [Number, String],
      default: null,
    },
    profileTitle: {
      type: String,
      default: '',
    },
    reference: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'referenceCreated', 'referenceUpdated'],
  setup(props, context) {
    const store = useStore();

    const currentStep = ref('form');
    const fileLoading = ref(false);
    const referenceFormSubmitted = ref(false);
    const successfullySubmitted = ref(false);
    const unknownError = ref(false);
    const fileList = ref([]);
    const { isMobile } = useBreakpoints();

    const currentProfileId = computed(() => props.profileId);
    const isEditMode = computed(() => Boolean(currentProfileId.value));
    const primaryButtonText = computed(() => (isEditMode.value ? 'Добавить' : 'Создать'));

    const sampleFile = `${process.env.VUE_APP_BACK}/storage/kpi_download_example.xlsx`;

    const parsedUsers = computed(() => store.state.references.parsedUsers);
    const parsedTotals = computed(() => parsedUsers.value?.totals ?? {});
    const hasParsedTotals = computed(() => Boolean(parsedUsers.value?.totals));

    const form = reactive({
      title: '',
    });

    const referenceTitle = computed(() => (
        props.profileTitle
        || form.title
        || parsedUsers.value?.report?.name
        || ''
    ));

    const statusError = ref({
      title: '',
    });

    const submitDisabled = computed(() => {
      if (fileLoading.value || !parsedTotals.value.success) {
        return true;
      }

      return !isEditMode.value && !form.title;
    });

    const clearErrors = () => {
      Object.keys(statusError.value).forEach((key) => {
        statusError.value[key] = '';
      });
    };

    const resetForm = () => {
      form.title = '';
      fileList.value = [];
      currentStep.value = 'form';
      successfullySubmitted.value = false;
      unknownError.value = false;
      clearErrors();
      store.commit('references/clearParsedUsers');
    };

    const closeForm = () => {
      resetForm();
      context.emit('close');
    };

    const beforeUpload = async (file) => {
      fileLoading.value = true;

      const formData = new FormData();
      formData.append('file', file);

      if (isEditMode.value) {
        formData.append('reference_profile_id', currentProfileId.value);
      }

      store.dispatch('references/parseUsersFromExcel', formData)
          .finally(() => {
            fileLoading.value = false;
          });

      fileList.value = [
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
        },
      ];

      return false;
    };

    const goToConfirmStep = () => {
      clearErrors();

      if (!isEditMode.value && !form.title) {
        statusError.value.title = 'Введите название';
        return;
      }

      if (!parsedTotals.value.success) {
        return;
      }

      currentStep.value = 'confirm';
    };

    const backToForm = () => {
      currentStep.value = 'form';
    };

    const submitReferenceProfile = () => {
      clearErrors();
      successfullySubmitted.value = false;
      unknownError.value = false;
      referenceFormSubmitted.value = true;

      const payload = {
        ...parsedUsers.value,
        filename: parsedUsers.value?.report?.name ?? fileList.value[0]?.name,
        report_name: referenceTitle.value,
        title: isEditMode.value ? null : form.title,
        profile_id: isEditMode.value ? currentProfileId.value : null,
        users: parsedUsers.value?.users ?? [],
      };

      store.dispatch('references/uploadUsersToReport', payload).then((data) => {
        if (data?.status === 422) {
          Object.entries(data.data ?? {}).forEach(([field, messages]) => {
            statusError.value[field] = Array.isArray(messages) ? messages[0] : messages;
          });
          currentStep.value = 'form';
          return;
        }

        if (data?.status && data.status >= 400) {
          unknownError.value = true;
        } else {
          successfullySubmitted.value = true;
          context.emit(isEditMode.value ? 'referenceUpdated' : 'referenceCreated');
        }

        currentStep.value = 'result';
      }).catch(() => {
        unknownError.value = true;
        currentStep.value = 'result';
      }).finally(() => {
        referenceFormSubmitted.value = false;
      });
    };

    return {
      backToForm,
      beforeUpload,
      closeForm,
      currentStep,
      fileList,
      fileLoading,
      form,
      goToConfirmStep,
      hasParsedTotals,
      isEditMode,
      isMobile,
      parsedTotals,
      parsedUsers,
      primaryButtonText,
      referenceTitle,
      referenceFormSubmitted,
      sampleFile,
      statusError,
      submitDisabled,
      submitReferenceProfile,
      successfullySubmitted,
      unknownError,
    };
  },
};
</script>

<style scoped lang="scss">
.upload-block {
  width: 100%;
}

.field-error {
  color: var(--dist-red-main);
  font-size: 12px;
}

.button-row {
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: $mobile-breakpoint) {
    flex-direction: column;
  }
}

.replace-btn,
.template-link {
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: rgba(0, 0, 0, 0.45);
}

.replace-btn:hover,
.template-link:hover {
  color: var(--main-green);
}

.after-upload {
  margin-top: 16px;
  padding: 16px;
  background-color: rgb(0, 0, 0, 0.02);
  border-radius: 8px;
}

.totals-row,
.result-box,
.error-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 16px;
  color: var(--grey-text);
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.total-row {
  margin-bottom: 4px;
}

.totals-row span {
  display: inline-flex;
  align-items: center;
  column-gap: 5px;
}

.total-row {
  color: var(--neutral-text, #262626);
  font-weight: 500;
}

.total-row b {
  font-weight: 700;
}

.total-row.totals-row {
  margin-bottom: 0;
}

.muted-status.has-warning b,
.error-status b {
  color: var(--dist-red-main);
}

.profile-status {
  font-size: 14px;
}

.error-box {
  margin-bottom: 10px;
}

.totals-divider {
  margin: 8px 0;
}

.info-icon,
.warning-icon {
  cursor: pointer;
}

.info-icon {
  color: rgba(0, 0, 0, 0.25);
}

.warning-icon {
  color: var(--dist-yellow-main, #faad14);
}

.download-report {
  display: inline-block;
  margin-top: 10px;
  color: var(--main-green);
}

.download-report:hover {
  color: var(--polar-green-3);
}

.confirm-text {
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
}

.confirm-text p {
  margin-bottom: 0;
}

.confirm-text span {
  font-size: 14px;
  font-weight: 400;
  color: var(--grey-text);
}

.confirm-details {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
}

.confirm-row {
  display: flex;
  column-gap: 4px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.confirm-row span {
  color: var(--grey-text);
}

.confirm-row b {
  text-align: right;
  word-break: break-word;
}

.successful-text {
  text-align: center;
  font-size: 16px;
  font-weight: 500;
}

.successful-text p {
  margin-top: 12px;
  margin-bottom: 0;
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  text-align: center;

  color: rgba(0, 0, 0, 0.88);
}

.successful-text span {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  text-align: center;

  color: rgba(0, 0, 0, 0.45);
}

.successful-text .happy-smile,
.successful-text .frown-smile {
  font-size: 65px;
}

.successful-text .happy-smile {
  color: var(--main-green);
}

.successful-text .frown-smile {
  color: var(--dist-red-main);
}

@media (max-width: 768px) {
  .button-row button {
    width: 100%;
  }

  .confirm-row {
    flex-direction: column;
  }

  .confirm-row b {
    text-align: left;
  }
}

:deep(.ant-form-item-label) {
  padding-bottom: 4px !important;
}

:deep(.ant-form-item) {
  margin-bottom: 12px;
}

.reference-profile-modal {
  h2 {
    margin: 0;
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
  }
}

.file-name {
  margin-left: 8px;
  color: var(--main-green)
}

.total-count {
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.back-button {
  margin-right: auto;
}

.confirm-row .confirm-data {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}


:deep(.ant-form-item-label),
:deep(.ant-form-item-required) {
  font-style: normal;
  font-weight: 400;
  font-size: 14px !important;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65) !important;
}
</style>
